<script setup lang="ts">
import { Loader2 } from 'lucide-vue-next';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import apiClient from '@/services/api_client';
import { createSharePointCredentials } from '@/services/auth_microsoft';
import { useAuthStore } from '@/stores/auth';
import { useMicrosoftConnectionStore } from '@/stores/microsoftConnectionStore';
import { useTenantStore } from '@/stores/tenant';

import SharePointCredentialsForm from './SharePointCredentialsForm.vue';

const tenantStore = useTenantStore();
const authStore = useAuthStore();
const microsoftConnectionStore = useMicrosoftConnectionStore();
const route = useRoute();
const router = useRouter();

const azureTenantId = ref<string>('');
const microsoftAppId = ref<string>('');
const microsoftAppPassword = ref<string>('');

const isSubmitting = ref<boolean>(false);
const isCredentialsLoading = ref<boolean>(true);
const areSharepointCredentialsSet = ref<boolean>(false);
const areInvalidCredentials = ref<boolean>(false);

const isConnectButtonDisabled = computed(() => {
    return (
        !tenantStore.tenantId ||
        !azureTenantId.value ||
        !microsoftAppId.value ||
        !microsoftAppPassword.value ||
        isSubmitting.value
    );
});

async function getSharePointCredentialsStatus() {
    if (!tenantStore.tenantId) {
        areSharepointCredentialsSet.value = false;
        isCredentialsLoading.value = false;
        return;
    }

    isCredentialsLoading.value = true;
    try {
        const credentials = await apiClient.getSharePointCredentials(
            tenantStore.tenantId
        );

        const hasCredentials =
            Boolean(credentials) && Object.keys(credentials).length > 0;
        areSharepointCredentialsSet.value = hasCredentials;

        if (!hasCredentials) {
            resetInputFields();
            return;
        }

        azureTenantId.value = credentials.azure_tenant_id;
        microsoftAppId.value = credentials.microsoft_app_id;
    } catch (error) {
        areSharepointCredentialsSet.value = false;
        resetInputFields();
        console.error(error);
    } finally {
        isCredentialsLoading.value = false;
    }
}

async function handleCreateSharePointCredentials() {
    if (isConnectButtonDisabled.value || !tenantStore.tenantId) {
        return;
    }

    isSubmitting.value = true;
    areInvalidCredentials.value = false;

    try {
        await createSharePointCredentials(
            tenantStore.tenantId,
            azureTenantId.value,
            microsoftAppId.value,
            microsoftAppPassword.value
        );

        areSharepointCredentialsSet.value = true;
        microsoftAppPassword.value = '';
        void microsoftConnectionStore.refresh();
        // A freshly registered application has no account in the MSAL cache, so the sign-in
        // below leaves the page. MSAL returns to the URL it left from, and the dialog is only
        // reopened by this parameter -- without it the admin comes back to a bare chat.
        await router.replace({
            query: { ...route.query, settingsTab: 'connections' }
        });
        // Signs in against the application that was just saved. Until it lands, the store has
        // no client id and no account, and the connectors below stay hidden.
        await authStore.updateCredentials(tenantStore.tenantId);
    } catch (error: unknown) {
        areInvalidCredentials.value = true;
        microsoftAppPassword.value = '';
        console.error('Error creating SharePoint credentials:', error);
        void microsoftConnectionStore.refresh();
    } finally {
        isSubmitting.value = false;
    }
}

const resetInputFields = () => {
    azureTenantId.value = '';
    microsoftAppId.value = '';
    microsoftAppPassword.value = '';
};

onMounted(() => {
    getSharePointCredentialsStatus();
    microsoftConnectionStore.ensureLoaded();
});

watch(
    () => tenantStore.tenantId,
    () => {
        areInvalidCredentials.value = false;
        getSharePointCredentialsStatus();
    }
);
</script>

<template>
    <div class="sharepoint-connection">
        <div v-if="isCredentialsLoading" class="text-sm text-muted-foreground">
            <Loader2 class="h-4 w-4 animate-spin" />
        </div>

        <SharePointCredentialsForm
            v-else
            v-model:microsoft-app-id="microsoftAppId"
            v-model:azure-tenant-id="azureTenantId"
            v-model:microsoft-app-password="microsoftAppPassword"
            :are-invalid-credentials="areInvalidCredentials"
            :are-sharepoint-credentials-set="areSharepointCredentialsSet"
            :connection-health="microsoftConnectionStore.health"
            :is-health-loading="microsoftConnectionStore.isLoading"
            :is-submit-disabled="isConnectButtonDisabled"
            @submit="handleCreateSharePointCredentials"
        />
    </div>
</template>
